import axios from "axios";
import queryString from "query-string";
import URLHelper from "../helpers/URLHelper";
import AuthServices from "./AuthServices";

/* 
roles:
'eira-super-admin'
'eira-company-admin'
'eira-company-coordinator'
'eira-professional'
'eira-patient'
*/

const token = AuthServices.getToken();
const UserServices = {
  getProfile: async (patient = null) => {
    const args =
      patient === null
        ? ""
        : `?who_user=${URLHelper.encodeQueryParamValue(patient)}`;
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/eira/my-contact-info${args}`
    );
    return response;
  },
  getManagement: async (id, data) => {
    const args = id === null ? "" : `id=${id}`;
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/management?${queryString.stringify(data)}&${args}`
    );
    return response;
  },
  saveManagement: async (data) => {
    const { enterprise, manager, position } = data;
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/eira/management",
      {
        enterprise,
        manager,
        position,
      }
    );

    return response;
  },
  deleteManagement: async (management_id) => {
    const response = await axios.delete(
      process.env.REACT_APP_API_URL + `/eira/management?id=${management_id}`
    );

    return response;
  },
  getCountries: async () => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/eira/country`
    );
    return response;
  },
  updateProfile: async (data) => {
    let formData = new FormData();

    formData.append("first_name", data.first_name);
    formData.append("middle_name", data.middle_name);
    formData.append("last_name", data.last_name);
    formData.append("country_code", data.country_code);
    formData.append("birth", data.birth);
    formData.append("identity_label", data.identity_label);
    formData.append("identity_value", data.identity_value);
    formData.append("biological_gender", data.biological_gender);
    formData.append(
      "phones",
      JSON.stringify(data.phones.filter((i) => i.number !== ""))
    );
    /* 
    tipos phones
    MOVIL = 'movil', _('Movil')
    FAX = 'fax', _('Fax')
    TRABAJO = 'trabajo', _('Trabajo')
    PRINCIPAL = 'principal', _('Principal')
    PRIVADO = 'privado', _('Privado')
    SIN_ETIQUETA = 'sin_etiqueta', _('Sin etiqueta')
    OTRO = 'otro', _('Otro')
    PERSONALIZADO = 'personalizado', _('Personalizado') */
    formData.append(
      "emails",
      JSON.stringify(data.emails.filter((i) => i.email !== ""))
    );
    /* 
    tipos de emails
    TRABAJO = 'trabajo', _('Trabajo')
    PRINCIPAL = 'principal', _('Principal')
    PRIVADO = 'privado', _('Privado')
    SIN_ETIQUETA = 'sin_etiqueta', _('Sin etiqueta')
    OTRO = 'otro', _('Otro')
    PERSONALIZADO = 'personalizado', _('Personalizado') */

    if (data.photo !== undefined) {
      formData.append("photo", data.photo);
    }

    if (data.num_professional_registration !== undefined) {
      formData.append(
        "num_professional_registration",
        data.num_professional_registration
      );
    }

    if (data.who_user !== undefined) {
      formData.append("who_user", data.who_user);
    }

    const response = await axios.put(
      process.env.REACT_APP_API_URL + `/eira/my-contact-info`,
      formData
    );
    return response;
  },
  getInvitation: async (enterprise_id, subscription_id, data) => {
    let args = enterprise_id === null ? "" : `?enterprise_id=${enterprise_id}`;
    if (subscription_id) {
      if (args != "") args += "&";
      args += `subscription_id=${subscription_id}`;
    }
    if (data) {
      args += args == "" ? "?" : "&";
      args += queryString.stringify(data);
    }
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/service/invite${args}`
    );

    return response;
  },
  resendInvitation: async (data) => {
    const { type, email, subscription } = data;
    const response = await axios.put(
      process.env.REACT_APP_API_URL + `/service/invite`,
      {
        type,
        email,
        subscription,
      }
    );

    return response;
  },
  sendInvitation: async (data) => {
    const response = await axios.put(
      process.env.REACT_APP_API_URL + "/service/invite",
      {
        type: data.type,
        subscription: data.subscription,
        email: data.email,
        rol: data.rol,
        app: data.app, // [eira | cronic]
        force: data.force,
      }
    );

    return response;
  },
  deleteInvitation: async (username) => {
    const response = await axios.delete(
      process.env.REACT_APP_API_URL +
        `/service/invite?username=${URLHelper.encodeQueryParamValue(username)}`
    );

    return response;
  },
  sendContactInvitation: async (data) => {
    const response = await axios.put(
      process.env.REACT_APP_API_URL + "/service/invite",
      {
        type: data.type,
        email: data.email,
        subscription: data.subscription_id,
      }
    );

    return response;
  },
  deleteContactInvitation: async (id) => {
    const response = await axios.delete(
      process.env.REACT_APP_API_URL + `/service/invite?invite_id=${id}`
    );

    return response;
  },
  getOutRangeMedicine: async (days, patient) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/out-of-range-medicine?days=${days}&patient=${URLHelper.encodeQueryParamValue(patient)}`
    );
    return response;
  },
  getOutRangeAutoevaluate: async (days, patient) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/out-of-range-survey?days=${days}&patient=${URLHelper.encodeQueryParamValue(patient)}`
    );
    return response;
  },
  assignMedicine: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/eira/assign-medicine",
      {
        vademecum_medicine: data.medicine_id,
        patient: data.patient,
        days: data.days,
        time: data.time,
        dosage: data.dosage,
        date_from: data.date_from,
        date_to: data.date_to,
      }
    );

    return response;
  },
  getAssignMedicine: async (patient, id, pagination) => {
    let args = id ? `&id=${id}` : "";
    if (pagination) {
      args = `&${queryString.stringify(pagination)}`;
    }
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/assign-medicine?patient=${URLHelper.encodeQueryParamValue(patient)}${args}`
    );

    return response;
  },
  deleteAssignMedicine: async (patient, medicine_id) => {
    const response = await axios.delete(
      process.env.REACT_APP_API_URL +
        `/eira/assign-medicine?patient=${URLHelper.encodeQueryParamValue(patient)}&vademecum_medicine=${medicine_id}&status=deleted`
    );

    return response;
  },
  sendNotification: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/service/intervention",
      {
        patient: data.patient,
        type: "notification",
        subscription_id: data.subscription_id,
        data: {
          title: data.title,
          body: data.body,
          data: {},
        },
      }
    );
    return response;
  },
  sendEmail: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/service/intervention",
      {
        patient: data.patient,
        type: "email",
        subscription_id: data.subscription_id,
        data: {
          subject: data.subject,
          body: data.body,
        },
      }
    );

    return response;
  },
  sendPill: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/service/intervention",
      {
        patient: data.patient,
        type: "pill",
        subscription_id: data.subscription_id,
        data: {
          pill_id: data.pill_id,
        },
      }
    );

    return response;
  },
  sendDocument: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/service/intervention",
      {
        patient: data.patient,
        title: data.title,
        type: "document",
        subscription_id: data.subscription_id,
        data: {
          body: data.body,
          file: data.file, //Requerido cuando sea "document" - No estoy seguro, tal vez sea un base64 como en Renuma
          data: {},
        },
      }
    );

    return response;
  },
  getPill: async (data) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/pill?${queryString.stringify(data)}`
    );

    return response;
  },
  getUserPills: async (username, days, data) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/intervention?patient=${URLHelper.encodeQueryParamValue(username)}&type=pill&days=${days}&${queryString.stringify(
          data
        )}`
    );

    return response;
  },
  getSubscription: async (patient) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/subscription?patient=${URLHelper.encodeQueryParamValue(patient)}`
    );

    return response;
  },
  getSubscriptionUser: async (data, subscription_id, rol) => {
    let args = rol ? `&rol=${rol}` : "";
    if (rol === "eira-patient") args += "&only_active=true";
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/subscription-user?subscription_id=${subscription_id}&${queryString.stringify(
          data
        )}${args}`
    );

    return response;
  },
  deleteSubscriptionUser: async (
    subscription_id,
    username,
    all = false,
    enterprise_id = null
  ) => {
    const args = queryString.stringify({
      subscription: subscription_id,
      username: username,
      all: all,
      ...(enterprise_id == null ? null : { enterprise: enterprise_id }),
    });

    const response = await axios.delete(
      process.env.REACT_APP_API_URL + `/eira/subscription-user?${args}`
    );

    return response;
  },
  getContacts: async () => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/eira/contacts`
    );
    return response;
  },
  getMyInvites: async (data) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/my-invites?${queryString.stringify(data)}`
    );
    return response;
  },
  getEndSubscriptions: async (enterprise_id, page_num, page_size) => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/end-subscriptions?enterprise_id=${enterprise_id}&page_num=${page_num}&page_size=${page_size}`
    );
    return response;
  },
  getInactiveContacts: async (
    days,
    subscription_id,
    enterprise_id = null,
    data = null
  ) => {
    let args = "";
    if (subscription_id !== null && subscription_id !== "") {
      args += `&subscription_id=${subscription_id}`;
    }
    if (enterprise_id !== null && enterprise_id !== "") {
      args += `&enterprise_id=${enterprise_id}`;
    }
    if (data) {
      args += `&${queryString.stringify(data)}`;
    }

    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/inactive-users?days=${days}${args}`
    );
    return response;
  },
  getBehindUser: async (
    days,
    subscription_id,
    enterprise_id = null,
    data = null
  ) => {
    let args = "";
    if (subscription_id !== null && subscription_id !== "") {
      args += `&subscription_id=${subscription_id}`;
    }
    if (enterprise_id !== null && enterprise_id !== "") {
      args += `&enterprise_id=${enterprise_id}`;
    }
    if (data) {
      args += `&${queryString.stringify(data)}`;
    }

    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/behind-users?days=${days}${args}`
    );

    return response;
  },
  getPatientInfo: async (patient) => {
    const _patient = patient ? URLHelper.encodeQueryParamValue(patient) : "";
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/service/patient-info?patient=${_patient}`
    );
    return response;
  },
  getPatientRange: async (patient) => {
    const _patient = patient ? URLHelper.encodeQueryParamValue(patient) : "";
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/eira/range?patient=${_patient}`
    );
    return response;
  },
  getNotes: async (subscription_id = null, patient = null) => {
    const _patient = patient ? URLHelper.encodeQueryParamValue(patient) : "";
    const args =
      subscription_id === null ? "" : `&subscription_id=${subscription_id}`;
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/eira/note?patient=${_patient}${args}`
    );
    return response;
  },
  getNotesCounter: async (subscription_id = null, patient = null) => {
    const _patient = patient ? URLHelper.encodeQueryParamValue(patient) : "";
    const args =
      subscription_id === null ? "" : `&subscription_id=${subscription_id}`;
    const response = await axios.get(
      process.env.REACT_APP_API_URL +
        `/eira/note/count?patient=${_patient}${args}`
    );
    return response;
  },
  newNote: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/eira/note",
      {
        patient: data.patient,
        subscription_id: data.subscription_id,
        title: data.title,
        body: data.body,
      }
    );

    return response;
  },
  matchRole: (rol) => {
    let res = "";
    switch (rol) {
      case "eira-super-admin":
        res = "Super admin";
        break;
      case "eira-company-admin":
        res = "Administrador";
        break;
      case "eira-company-coordinator":
        res = "Coordinador";
        break;
      case "eira-professional":
        res = "Profesional";
        break;
      case "eira-patient":
        res = "Paciente";
        break;
    }
    return res;
  },
  getResumeFileUrl: async (
    from_date = null,
    to_date = null,
    patient = null,
    type = null,
    measureType = "",
    format_file = "pdf"
  ) => {
    const _patient = patient ? URLHelper.encodeQueryParamValue(patient) : "";
    let args = `from_date=${from_date}&to_date=${to_date}&patient=${_patient}&type=${type}&format_file=${format_file}`;
    if (measureType !== "") {
      args += `&medicion_type=${measureType}`;
    }
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/service/personal-monitoring?${args}`
    );
    return response;
  },
  updateInvitePermissions: async (data) => {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/service/invite-permission",
      {
        username: data.username,
        can_invite: data.can_invite,
        subscription_id: data.subscription_id,
        enterprise_id: data.enterprise_id,
      }
    );

    return response;
  },
  recieveEmailNewUsers: async (data) => {
    const response = await axios.put(
      process.env.REACT_APP_API_URL + "/eira/management",
      {
        manager: data.manager,
        receive_email_new_users: data.receive_email_new_users,
        enterprise: data.enterprise,
      }
    );

    return response;
  },

  receiveEmailNewUserPut: async (data) => {
    const response = await axios.put(
      process.env.REACT_APP_API_URL + "/service/receive-email-new-users",
      {
        username: data.username,
        subscription_id: data.subscription_id,
        enterprise_id: data.enterprise_id,
        receive_email_new_users: data.receive_email_new_users,
      }
    );
    return response;
  },
};

export default UserServices;
